import { useToast } from '@chakra-ui/react';
import { DrawerType } from '@pelicargo/types';
import { RequestStatus } from '@prisma/client';
import { useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useRequestDrawersContext } from '../components/RequestDrawers';
import { trpc } from '../config/trpc';
import { getCustomerUrl } from '../utils/get-url';
import { GetRequestOutput } from './request/useRequest';

export const useRequestEmails = ({ request }: { request: GetRequestOutput }) => {
  const toast = useToast();
  const { openModal } = useRequestDrawersContext();

  const { mutateAsync: assertRequestIsValidTo, isLoading: isLoadingBookingAssertion } =
    trpc.assertRequestIsValidTo.useMutation();

  const quoteListingSlug = useMemo(() => {
    return `/requests/${request?.id}`;
  }, [request?.id]);
  const bookingConfirmationSlug = useMemo(() => {
    return request?.booked_quote_variant?.quote_id
      ? `/requests/${request?.id}/quotes/${request.booked_quote_variant.quote_id}/booked`
      : null;
  }, [request?.booked_quote_variant?.quote_id, request?.id]);
  const marketplaceBookingSlug = useMemo(() => {
    return request?.booked_quote_variant?.quote_id &&
      request?.booked_quote_variant?.quote?.fulfillment_partner_configuration_id
      ? `/marketplace/${request?.id}/quotes/${request.booked_quote_variant.quote_id}/booked`
      : null;
  }, [
    request?.booked_quote_variant?.quote_id,
    request?.booked_quote_variant?.quote?.fulfillment_partner_configuration_id,
    request?.id,
  ]);

  const copyLink = (slug?: string | null) => () => {
    if (!slug) return;
    const url = getCustomerUrl(slug);

    navigator.clipboard.writeText(url);

    toast({
      title: 'Link Copied',
      description: url,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  useHotkeys(
    'l>q',
    () => {
      copyLink(quoteListingSlug)();
    },
    { preventDefault: true },
  );

  useHotkeys(
    'l>b',
    () => {
      if (!bookingConfirmationSlug) return;
      copyLink(bookingConfirmationSlug)();
    },
    { preventDefault: true, enabled: !!bookingConfirmationSlug },
  );

  useHotkeys(
    'l>m',
    () => {
      if (!marketplaceBookingSlug) return;
      copyLink(marketplaceBookingSlug)();
    },
    { preventDefault: true, enabled: !!marketplaceBookingSlug },
  );

  const handleClickSendBooking = async () => {
    try {
      await assertRequestIsValidTo({ id: request.id, desiredStatus: RequestStatus.BookingComplete });
      openModal('BookingConfirmation');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
    }
  };

  const handleQuickSelectDrawer = (drawerType: DrawerType) => {
    switch (drawerType) {
      case DrawerType.BookingConfirmation:
        return handleClickSendBooking();
      case DrawerType.RequestSchedule:
        if (!request?.dropoff_by) {
          return toast({
            title: 'Error',
            description: "No 'dropoff by' date is set",
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return openModal(drawerType);
    }
  };

  return {
    isLoadingBookingAssertion,
    handleClickSendBooking,
    handleQuickSelectDrawer,
    copyLink,

    quoteListingSlug,
    bookingConfirmationSlug,
    marketplaceBookingSlug,
  };
};
